import React from "react";
import { ChevronDown, ChevronUp, Lock, Unlock, Sparkles } from "lucide-react";
import {
    subIndustryOptionsByIndustry,
    industryOptions,
    goalOptions,
    toneOptions,
    ctaOptions,
    brandStyleOptions,
    pricePositioningOptions
} from "../../data/websiteOptions";
import PagesEditor from "./PagesEditor";

function Field({
    label,
    children,
    full = false,
    note = "",
    source = "",
    lockable = false,
    locked = false,
    onToggleLock
}) {
    return (
        <div className={`wg-field ${full ? "wg-field-full" : ""}`}>
            <div className="wg-label-row">
                <div className="wg-label-meta">
                    <label className="wg-label">{label}</label>

                    {source === "ai" && (
                        <span className="wg-field-badge wg-field-badge-ai">AI suggested</span>
                    )}

                    {source === "manual" && (
                        <span className="wg-field-badge wg-field-badge-manual">Edited</span>
                    )}
                </div>

                {lockable && (
                    <button
                        type="button"
                        className={`wg-lock-btn ${locked ? "locked" : ""}`}
                        onClick={onToggleLock}
                    >
                        {locked ? <Lock size={14} /> : <Unlock size={14} />}
                        <span>{locked ? "Locked" : "AI can update"}</span>
                    </button>
                )}
            </div>

            {children}
            {note ? <div className="wg-field-note">{note}</div> : null}
        </div>
    );
}

function TwoCol({ children }) {
    return <div className="wg-grid-2">{children}</div>;
}

function SectionHeader({ title, subtitle, badge = "" }) {
    return (
        <div className="wg-block-header">
            <div>
                {badge ? <div className="wg-badge-subtle">{badge}</div> : null}
                <h3 className="wg-block-title">{title}</h3>
                <p className="wg-block-subtitle">{subtitle}</p>
            </div>
        </div>
    );
}

function CollapseHeader({
    title,
    subtitle,
    badge = "",
    isOpen,
    onToggle,
    actionText = ""
}) {
    return (
        <div className="wg-collapse-head">
            <div className="wg-collapse-copy">
                {badge ? <div className="wg-badge-subtle">{badge}</div> : null}
                <h3 className="wg-block-title">{title}</h3>
                <p className="wg-block-subtitle">{subtitle}</p>
            </div>

            <div className="wg-collapse-actions">
                {actionText ? <span className="wg-collapse-hint">{actionText}</span> : null}

                <button
                    type="button"
                    className="wg-secondary-btn"
                    onClick={onToggle}
                >
                    {isOpen ? (
                        <>
                            <ChevronUp size={16} />
                            Hide
                        </>
                    ) : (
                        <>
                            <ChevronDown size={16} />
                            Show
                        </>
                    )}
                </button>
            </div>
        </div>
    );
}

export default function BusinessDetailsStep({
    form,
    updateField,
    onAutofill,
    autofilling,
    mode,
    setMode,
    showOfferings,
    setShowOfferings,
    showAdvanced,
    setShowAdvanced,
    fieldSourceMap = {},
    lockedAiFields = {},
    onToggleAiFieldLock
}) {
    const offeredItems = Array.isArray(form.offeredItems) ? form.offeredItems : [];
    const [showAiSuggestionsPanel, setShowAiSuggestionsPanel] = React.useState(false);

    const updateOfferedItem = (index, key, value) => {
        const next = [...offeredItems];
        next[index] = {
            ...next[index],
            [key]: value
        };
        updateField("offeredItems", next);
    };

    const addOfferedItem = () => {
        updateField("offeredItems", [
            ...offeredItems,
            {
                id: crypto.randomUUID(),
                name: "",
                subtitle: "",
                description: "",
                badge: "",
                category: "",
                audience: "",
                level: "",
                imagePreference: "",
                keyPoints: [""]
            }
        ]);
    };

    const removeOfferedItem = (index) => {
        const next = offeredItems.filter((_, i) => i !== index);
        updateField(
            "offeredItems",
            next.length > 0
                ? next
                : [
                    {
                        id: crypto.randomUUID(),
                        name: "",
                        subtitle: "",
                        description: "",
                        badge: "",
                        category: "",
                        audience: "",
                        level: "",
                        imagePreference: "",
                        keyPoints: [""]
                    }
                ]
        );
    };

    const addKeyPoint = (itemIndex) => {
        const next = [...offeredItems];
        next[itemIndex] = {
            ...next[itemIndex],
            keyPoints: [...(next[itemIndex].keyPoints || []), ""]
        };
        updateField("offeredItems", next);
    };

    const updateKeyPoint = (itemIndex, pointIndex, value) => {
        const next = [...offeredItems];
        const points = [...(next[itemIndex].keyPoints || [])];
        points[pointIndex] = value;
        next[itemIndex] = {
            ...next[itemIndex],
            keyPoints: points
        };
        updateField("offeredItems", next);
    };

    const removeKeyPoint = (itemIndex, pointIndex) => {
        const next = [...offeredItems];
        const points = [...(next[itemIndex].keyPoints || [])].filter((_, i) => i !== pointIndex);
        next[itemIndex] = {
            ...next[itemIndex],
            keyPoints: points.length > 0 ? points : [""]
        };
        updateField("offeredItems", next);
    };

    const hasAiStrategy =
        !!form.businessModel ||
        !!form.primaryWebsiteMode ||
        !!form.primaryEntityType ||
        !!form.locationFocus ||
        !!form.contentStyleHint ||
        !!form.themeKey ||
        !!form.callToActionType ||
        !!form.brandStyle ||
        !!form.pricePositioning ||
        (Array.isArray(form.pages) && form.pages.length > 0);

    const hasOfferedItemsContent = offeredItems.some(
        (item) =>
            item?.name?.trim() ||
            item?.subtitle?.trim() ||
            item?.description?.trim() ||
            item?.badge?.trim() ||
            item?.category?.trim() ||
            item?.audience?.trim() ||
            item?.level?.trim() ||
            item?.imagePreference?.trim() ||
            (Array.isArray(item?.keyPoints) && item.keyPoints.some((x) => x?.trim()))
    );

    const essentialsReady =
        !!form.businessName?.trim() &&
        !!form.industry?.trim() &&
        !!form.description?.trim() &&
        !!form.goalOfWebsite?.trim();

    const shouldShowOfferings = showOfferings || hasOfferedItemsContent;
    const shouldShowAiSuggestions = showAiSuggestionsPanel || autofilling;
    const shouldShowAdvanced = mode === "detailed" || showAdvanced;

    React.useEffect(() => {
        if (autofilling) {
            setShowAiSuggestionsPanel(true);
        }
    }, [autofilling]);

    return (
        <div className="wg-step-shell">
            <div className="wg-mode-bar">
                <div>
                    <h2 className="wg-section-title">Business Details</h2>
                    <p className="wg-section-subtitle">
                        Start with the business truth first. Then let AI suggest the
                        best structure, strategy, and styling for your industry.
                    </p>
                </div>

                <div className="wg-mode-toggle">
                    <button
                        type="button"
                        className={`wg-chip-btn ${mode === "fast" ? "active" : ""}`}
                        onClick={() => setMode("fast")}
                    >
                        Fast Mode
                    </button>
                    <button
                        type="button"
                        className={`wg-chip-btn ${mode === "detailed" ? "active" : ""}`}
                        onClick={() => setMode("detailed")}
                    >
                        Detailed Mode
                    </button>
                </div>
            </div>

            {essentialsReady ? (
                <div className="wg-ai-banner">
                    <div>
                        <strong>AI Suggestions</strong>
                        <p>
                            AI can now prepare strategy, pages, tone, and layout direction from your essentials.
                            You can still edit everything after autofill.
                        </p>
                    </div>

                    <button
                        type="button"
                        className="wg-primary-btn"
                        onClick={onAutofill}
                        disabled={autofilling}
                    >
                        {autofilling ? "Analyzing Business..." : hasAiStrategy ? "Refresh AI Suggestions" : "Generate AI Suggestions"}
                    </button>
                </div>
            ) : (
                <div className="wg-ai-prep-card">
                    <div className="wg-ai-prep-badge">
                        <Sparkles size={16} />
                        <span>AI Assistant Ready</span>
                    </div>

                    <h3 className="wg-ai-prep-title">Fill the essentials to unlock AI suggestions</h3>

                    <p className="wg-ai-prep-subtitle">
                        Once you add your business name, industry, description, and goal, AI will help
                        classify your business, suggest structure, recommend pages, and prepare strategy fields.
                    </p>
                </div>
            )}

            <div className="wg-block">
                <SectionHeader
                    title="Essentials"
                    subtitle="These fields help AI understand what the business is, what it offers, and what the website should achieve."
                    badge="Required first"
                />

                <TwoCol>
                    <Field label="Business Name">
                        <input
                            type="text"
                            value={form.businessName}
                            onChange={(e) => updateField("businessName", e.target.value)}
                            placeholder="e.g. Shriansh AI"
                        />
                    </Field>

                    <Field label="Industry">
                        <select
                            value={form.industry}
                            onChange={(e) => {
                                const nextIndustry = e.target.value;
                                updateField("industry", nextIndustry);

                                const nextSuggestions = subIndustryOptionsByIndustry[nextIndustry] || [];
                                if (
                                    nextSuggestions.length > 0 &&
                                    !nextSuggestions.includes(form.subIndustry)
                                ) {
                                    updateField("subIndustry", "");
                                }
                            }}
                        >
                            <option value="">Select Industry</option>
                            {industryOptions.map((option) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </select>
                    </Field>
                </TwoCol>

                <TwoCol>
                    <Field label="Sub Industry" source={fieldSourceMap.subIndustry}>
                        {subIndustryOptionsByIndustry[form.industry]?.length > 0 ? (
                            <div className="wg-stack-sm">
                                <select
                                    value={form.subIndustry}
                                    onChange={(e) => updateField("subIndustry", e.target.value)}
                                >
                                    <option value="">Select Sub Industry</option>
                                    {subIndustryOptionsByIndustry[form.industry].map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>

                                <input
                                    type="text"
                                    value={form.subIndustry}
                                    onChange={(e) => updateField("subIndustry", e.target.value)}
                                    placeholder="Or type a custom sub industry"
                                />
                            </div>
                        ) : (
                            <input
                                type="text"
                                value={form.subIndustry}
                                onChange={(e) => updateField("subIndustry", e.target.value)}
                                placeholder="e.g. AI Automation, CRM, Property Advisory, Football Training"
                            />
                        )}
                    </Field>

                    <Field label="Website Goal">
                        <select
                            value={form.goalOfWebsite}
                            onChange={(e) => updateField("goalOfWebsite", e.target.value)}
                        >
                            <option value="">Select Goal</option>
                            {goalOptions.map((option) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </select>
                    </Field>
                </TwoCol>

                <TwoCol>
                    <Field label="Location">
                        <input
                            type="text"
                            value={form.location}
                            onChange={(e) => updateField("location", e.target.value)}
                            placeholder="e.g. Ahmedabad, India"
                        />
                    </Field>

                    <Field
                        label="Main Audience"
                        note="Optional, but helpful if you already know your ideal customers."
                        source={fieldSourceMap.primaryAudience}
                    >
                        <input
                            type="text"
                            value={form.primaryAudience}
                            onChange={(e) => updateField("primaryAudience", e.target.value)}
                            placeholder="e.g. Home buyers, parents, patients, startups"
                        />
                    </Field>
                </TwoCol>

                <Field
                    label="Business Description"
                    full
                    note="Describe what the business does, what it offers, and why customers choose it."
                >
                    <textarea
                        value={form.description}
                        onChange={(e) => updateField("description", e.target.value)}
                        placeholder="Describe what the business does, who it helps, and what makes it valuable."
                        rows={5}
                    />
                </Field>

                <TwoCol>
                    <Field
                        label="Main Offerings"
                        note="Main services, products, or solutions the business sells."
                        source={fieldSourceMap.coreServices}
                    >
                        <textarea
                            value={form.coreServices}
                            onChange={(e) => updateField("coreServices", e.target.value)}
                            placeholder="e.g. Property buying, leasing, legal consultation, SaaS automation"
                            rows={4}
                        />
                    </Field>

                    <Field
                        label="Service / Product Types"
                        note="Optional categories or segments that help AI understand your business structure."
                        source={fieldSourceMap.projectTypes}
                    >
                        <textarea
                            value={form.projectTypes}
                            onChange={(e) => updateField("projectTypes", e.target.value)}
                            placeholder="e.g. Apartments, villas, youth academy, clinic services, product plans"
                            rows={4}
                        />
                    </Field>
                </TwoCol>
            </div>

            <div className="wg-block">
                <CollapseHeader
                    title="Offerings / Products / Programs"
                    subtitle="Optional, but powerful. These items help AI build more realistic sections, showcase blocks, and page recommendations."
                    badge="Optional but high impact"
                    isOpen={shouldShowOfferings}
                    onToggle={() => setShowOfferings((prev) => !prev)}
                    actionText={hasOfferedItemsContent ? "You already added offering data" : ""}
                />

                {shouldShowOfferings && (
                    <>
                        <div className="wg-offering-topbar">
                            <button
                                type="button"
                                className="wg-secondary-btn"
                                onClick={addOfferedItem}
                            >
                                + Add Item
                            </button>
                        </div>

                        {offeredItems.map((item, itemIndex) => (
                            <div className="wg-offering-card" key={item.id || itemIndex}>
                                <div className="wg-offering-head">
                                    <h4>Item {itemIndex + 1}</h4>
                                    <button
                                        type="button"
                                        className="wg-danger-btn"
                                        onClick={() => removeOfferedItem(itemIndex)}
                                    >
                                        Remove
                                    </button>
                                </div>

                                <TwoCol>
                                    <Field label="Name">
                                        <input
                                            type="text"
                                            value={item.name || ""}
                                            onChange={(e) =>
                                                updateOfferedItem(itemIndex, "name", e.target.value)
                                            }
                                        />
                                    </Field>

                                    <Field label="Subtitle">
                                        <input
                                            type="text"
                                            value={item.subtitle || ""}
                                            onChange={(e) =>
                                                updateOfferedItem(itemIndex, "subtitle", e.target.value)
                                            }
                                        />
                                    </Field>
                                </TwoCol>

                                <TwoCol>
                                    <Field label="Badge">
                                        <input
                                            type="text"
                                            value={item.badge || ""}
                                            onChange={(e) =>
                                                updateOfferedItem(itemIndex, "badge", e.target.value)
                                            }
                                        />
                                    </Field>

                                    <Field label="Category">
                                        <input
                                            type="text"
                                            value={item.category || ""}
                                            onChange={(e) =>
                                                updateOfferedItem(itemIndex, "category", e.target.value)
                                            }
                                        />
                                    </Field>
                                </TwoCol>

                                <TwoCol>
                                    <Field label="Audience">
                                        <input
                                            type="text"
                                            value={item.audience || ""}
                                            onChange={(e) =>
                                                updateOfferedItem(itemIndex, "audience", e.target.value)
                                            }
                                        />
                                    </Field>

                                    <Field label="Level / Type">
                                        <input
                                            type="text"
                                            value={item.level || ""}
                                            onChange={(e) =>
                                                updateOfferedItem(itemIndex, "level", e.target.value)
                                            }
                                        />
                                    </Field>
                                </TwoCol>

                                <Field label="Image Preference" full>
                                    <input
                                        type="text"
                                        value={item.imagePreference || ""}
                                        onChange={(e) =>
                                            updateOfferedItem(itemIndex, "imagePreference", e.target.value)
                                        }
                                        placeholder="e.g. premium product shot, clinic interior, coach with students, property exterior"
                                    />
                                </Field>

                                <Field label="Description" full>
                                    <textarea
                                        value={item.description || ""}
                                        onChange={(e) =>
                                            updateOfferedItem(itemIndex, "description", e.target.value)
                                        }
                                        rows={4}
                                    />
                                </Field>

                                <div className="wg-keypoints-head">
                                    <h5>Key Points</h5>
                                    <button
                                        type="button"
                                        className="wg-secondary-btn"
                                        onClick={() => addKeyPoint(itemIndex)}
                                    >
                                        + Add Point
                                    </button>
                                </div>

                                {(item.keyPoints || []).map((point, pointIndex) => (
                                    <div className="wg-keypoint-row" key={`${itemIndex}-${pointIndex}`}>
                                        <input
                                            type="text"
                                            value={point || ""}
                                            onChange={(e) =>
                                                updateKeyPoint(itemIndex, pointIndex, e.target.value)
                                            }
                                            placeholder={`Point ${pointIndex + 1}`}
                                        />
                                        <button
                                            type="button"
                                            className="wg-danger-btn"
                                            onClick={() => removeKeyPoint(itemIndex, pointIndex)}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </>
                )}
            </div>

            <div className="wg-block">
                <CollapseHeader
                    title="AI Suggestions"
                    subtitle="These fields are ideal for AI-assisted classification. Let AI suggest them, then adjust only if needed."
                    badge="AI fills these best"
                    isOpen={shouldShowAiSuggestions}
                    onToggle={() => {
                        if (!essentialsReady) return;

                        if (!hasAiStrategy && !autofilling) {
                            onAutofill();
                            setShowAiSuggestionsPanel(true);
                            return;
                        }

                        setShowAiSuggestionsPanel((prev) => !prev);
                    }}
                    actionText={
                        !essentialsReady
                            ? "Complete essentials first"
                            : hasAiStrategy
                                ? (showAiSuggestionsPanel ? "Strategy suggestions visible" : "Strategy suggestions ready")
                                : "Generate suggestions to continue"
                    }
                />

                {shouldShowAiSuggestions && (
                    <>
                        <TwoCol>
                            <Field
                                label="Business Type"
                                source={fieldSourceMap.businessModel}
                                lockable
                                locked={!!lockedAiFields.businessModel}
                                onToggleLock={() => onToggleAiFieldLock("businessModel")}
                                note="Examples: service business, academy, marketplace, SaaS, local business"
                            >
                                <input
                                    type="text"
                                    value={form.businessModel}
                                    onChange={(e) => updateField("businessModel", e.target.value)}
                                    placeholder="AI will suggest this"
                                />
                            </Field>

                            <Field
                                label="Website Focus"
                                source={fieldSourceMap.primaryWebsiteMode}
                                lockable
                                locked={!!lockedAiFields.primaryWebsiteMode}
                                onToggleLock={() => onToggleAiFieldLock("primaryWebsiteMode")}
                                note="Examples: lead-first, search-first, conversion-first, program-first, product-first"
                            >
                                <input
                                    type="text"
                                    value={form.primaryWebsiteMode}
                                    onChange={(e) => updateField("primaryWebsiteMode", e.target.value)}
                                    placeholder="AI will suggest this"
                                />
                            </Field>
                        </TwoCol>

                        <TwoCol>
                            <Field
                                label="Main Content Type"
                                source={fieldSourceMap.primaryEntityType}
                                lockable
                                locked={!!lockedAiFields.primaryEntityType}
                                onToggleLock={() => onToggleAiFieldLock("primaryEntityType")}
                                note="Examples: services, listings, programs, products, features"
                            >
                                <input
                                    type="text"
                                    value={form.primaryEntityType}
                                    onChange={(e) => updateField("primaryEntityType", e.target.value)}
                                    placeholder="AI will suggest this"
                                />
                            </Field>

                            <Field
                                label="Service Coverage"
                                source={fieldSourceMap.locationFocus}
                                lockable
                                locked={!!lockedAiFields.locationFocus}
                                onToggleLock={() => onToggleAiFieldLock("locationFocus")}
                                note="Examples: local, regional, national, global"
                            >
                                <input
                                    type="text"
                                    value={form.locationFocus}
                                    onChange={(e) => updateField("locationFocus", e.target.value)}
                                    placeholder="AI will suggest this"
                                />
                            </Field>
                        </TwoCol>

                        <TwoCol>
                            <Field
                                label="Content Direction"
                                source={fieldSourceMap.contentStyleHint}
                                lockable
                                locked={!!lockedAiFields.contentStyleHint}
                                onToggleLock={() => onToggleAiFieldLock("contentStyleHint")}
                                note="Examples: trust-heavy, premium-minimal, utility-dense, conversion-first"
                            >
                                <input
                                    type="text"
                                    value={form.contentStyleHint}
                                    onChange={(e) => updateField("contentStyleHint", e.target.value)}
                                    placeholder="AI will suggest this"
                                />
                            </Field>

                            <Field
                                label="Call To Action"
                                source={fieldSourceMap.callToActionType}
                                lockable
                                locked={!!lockedAiFields.callToActionType}
                                onToggleLock={() => onToggleAiFieldLock("callToActionType")}
                                note="AI can suggest the best CTA for your business goal."
                            >
                                <select
                                    value={form.callToActionType}
                                    onChange={(e) => updateField("callToActionType", e.target.value)}
                                >
                                    <option value="">Select CTA</option>
                                    {ctaOptions.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                        </TwoCol>

                        <TwoCol>
                            <Field
                                label="Tone"
                                source={fieldSourceMap.tone}
                                lockable
                                locked={!!lockedAiFields.tone}
                                onToggleLock={() => onToggleAiFieldLock("tone")}
                            >
                                <select
                                    value={form.tone}
                                    onChange={(e) => updateField("tone", e.target.value)}
                                >
                                    <option value="">Select Tone</option>
                                    {toneOptions.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                            </Field>

                            <Field
                                label="Brand Style"
                                source={fieldSourceMap.brandStyle}
                                lockable
                                locked={!!lockedAiFields.brandStyle}
                                onToggleLock={() => onToggleAiFieldLock("brandStyle")}
                            >
                                <select
                                    value={form.brandStyle}
                                    onChange={(e) => updateField("brandStyle", e.target.value)}
                                >
                                    <option value="">Select Brand Style</option>
                                    {brandStyleOptions.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                        </TwoCol>

                        <TwoCol>
                            <Field
                                label="Price Positioning"
                                source={fieldSourceMap.pricePositioning}
                                lockable
                                locked={!!lockedAiFields.pricePositioning}
                                onToggleLock={() => onToggleAiFieldLock("pricePositioning")}
                            >
                                <select
                                    value={form.pricePositioning}
                                    onChange={(e) => updateField("pricePositioning", e.target.value)}
                                >
                                    <option value="">Select Positioning</option>
                                    {pricePositioningOptions.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                            </Field>

                            <Field
                                label="Theme"
                                source={fieldSourceMap.themeKey}
                                lockable
                                locked={!!lockedAiFields.themeKey}
                                onToggleLock={() => onToggleAiFieldLock("themeKey")}
                                note="Usually AI should suggest this from industry + strategy."
                            >
                                <input
                                    type="text"
                                    value={form.themeKey}
                                    onChange={(e) => updateField("themeKey", e.target.value)}
                                    placeholder="AI will suggest this"
                                />
                            </Field>
                        </TwoCol>

                        <Field
                            label="Recommended Pages"
                            full
                            source={fieldSourceMap.pages}
                            note="These page suggestions can be generated automatically from the business type."
                        >
                            <PagesEditor
                                pages={form.pages || []}
                                locked={!!lockedAiFields.pages}
                                onToggleLock={() => onToggleAiFieldLock("pages")}
                                onChange={(nextPages) => updateField("pages", nextPages)}
                            />
                        </Field>
                    </>
                )}
            </div>

            <div className="wg-block">
                <CollapseHeader
                    title="Advanced Refinement"
                    subtitle="Use these only if you want tighter control. Otherwise, let AI infer and refine them for you."
                    badge="Optional"
                    isOpen={shouldShowAdvanced}
                    onToggle={() => setShowAdvanced((prev) => !prev)}
                    actionText={mode === "detailed" ? "Detailed mode enabled" : ""}
                />

                {shouldShowAdvanced && (
                    <>
                        <TwoCol>
                            <Field label="Secondary Audience">
                                <input
                                    type="text"
                                    value={form.secondaryAudience}
                                    onChange={(e) => updateField("secondaryAudience", e.target.value)}
                                />
                            </Field>

                            <Field label="Service Areas">
                                <input
                                    type="text"
                                    value={form.serviceAreas}
                                    onChange={(e) => updateField("serviceAreas", e.target.value)}
                                />
                            </Field>
                        </TwoCol>

                        <Field label="Unique Selling Points" full>
                            <textarea
                                value={form.uniqueSellingPoints}
                                onChange={(e) => updateField("uniqueSellingPoints", e.target.value)}
                                rows={4}
                            />
                        </Field>

                        <Field label="Trust Signals" full>
                            <textarea
                                value={form.trustSignals}
                                onChange={(e) => updateField("trustSignals", e.target.value)}
                                rows={4}
                            />
                        </Field>

                        <TwoCol>
                            <Field label="Years in Business">
                                <input
                                    type="text"
                                    value={form.yearsInBusiness}
                                    onChange={(e) => updateField("yearsInBusiness", e.target.value)}
                                />
                            </Field>

                            <Field label="Reference Website Type">
                                <input
                                    type="text"
                                    value={form.referenceWebsiteType}
                                    onChange={(e) => updateField("referenceWebsiteType", e.target.value)}
                                />
                            </Field>
                        </TwoCol>

                        <TwoCol>
                            <Field label="Desired Layout Preference">
                                <input
                                    type="text"
                                    value={form.desiredLayoutStyle}
                                    onChange={(e) => updateField("desiredLayoutStyle", e.target.value)}
                                />
                            </Field>

                            <Field label="Visual Tone">
                                <input
                                    type="text"
                                    value={form.visualTone}
                                    onChange={(e) => updateField("visualTone", e.target.value)}
                                />
                            </Field>
                        </TwoCol>

                        <Field label="Preferred Section Density" full>
                            <input
                                type="text"
                                value={form.preferredSectionDensity}
                                onChange={(e) => updateField("preferredSectionDensity", e.target.value)}
                            />
                        </Field>

                        <TwoCol>
                            <Field label="Must Include Words">
                                <input
                                    type="text"
                                    value={form.mustIncludeWords}
                                    onChange={(e) => updateField("mustIncludeWords", e.target.value)}
                                />
                            </Field>

                            <Field label="Avoid Words">
                                <input
                                    type="text"
                                    value={form.avoidWords}
                                    onChange={(e) => updateField("avoidWords", e.target.value)}
                                />
                            </Field>
                        </TwoCol>

                        <Field label="Competitor Website URL" full>
                            <input
                                type="text"
                                value={form.competitorWebsiteUrl}
                                onChange={(e) => updateField("competitorWebsiteUrl", e.target.value)}
                            />
                        </Field>

                        <Field label="Competitor Style Notes" full>
                            <textarea
                                value={form.competitorStyleNotes}
                                onChange={(e) => updateField("competitorStyleNotes", e.target.value)}
                                rows={3}
                            />
                        </Field>
                    </>
                )}
            </div>
        </div>
    );
}